import { Injectable, OnDestroy } from '@angular/core';
import { Models } from '@ibexfusion/models';

import { ContentPipesByContentType } from '../../maps/content-pipes.map';
import { ContentService } from '../content/content.service';
import { TranslateService } from '../translate/translate.service';
import { NavigationItem } from '../navigation-item/NavigationItem';
import { NavigationItemService } from '../navigation-item/navigation-item.service';
import { Router } from '@angular/router';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class ContentPipeService implements OnDestroy {

  private readySubscription;
  private ready: boolean;
  private url;

  private transformCache = {};

  constructor(private contentService: ContentService, private translateService: TranslateService, private navService: NavigationItemService, private router: Router) {
    this.readySubscription = this.navService.$ready.subscribe(ready => {
      this.ready = ready;
    });
  }

  getTransformedEntry<T extends Models.IbexBaseModel>(entry: Models.ContentEntry): T & T[] {
    if (!entry?.detail?.contentType) {
      return null;
    }

    const pipe = ContentPipesByContentType.get(entry.detail.contentType);

    if (!pipe) {
      return null;
    }
    let data;
    try {
      data = new pipe(this.contentService, this.translateService).transform(entry);
    } catch (err) {
      console.warn(err);
    }
    return data;
  }

  getTransformedEntryByUrl<T extends Models.IbexBaseModel>(force = false): { content: T & T[], navItem: NavigationItem } {
    const outlet = this.router.url.includes('(app:') ? '(app:' : '(pages:';
    const urlParts = this.router.url.split(outlet);
    if (!urlParts) {
      return null;
    }
    let url = urlParts.length > 1 ? urlParts[1].split(')')[0] : null;
    if (!url) {
      return null;
    }
    const urlPartsCount = url.split('/');
    if (urlPartsCount[urlPartsCount.length - 1].match(/^\d+$/)) {
      url = urlPartsCount.slice(0, -1).join('/');
    }
    if (!url) {
      return null;
    }
    if (url.split('/')[2]) {
      url = [url.split('/')[0], url.split('/')[1], url.split('/')[2]].join('/');
    } else {
      url = [url.split('/')[0], url.split('/')[1]].join('/');
    }
    if (!url || (!this.ready && !force)) {
      return null;
    }
    let parentUrlParts = urlParts[0].split('/');
    let parentUrl;
    for (let parentPart of parentUrlParts) {
      if (parentPart.length > 0) {
        parentUrl = parentPart;
      }
    }
    let cacheKey = parentUrl + url + this.translateService.getCurrentLanguage();
    if (this.transformCache[cacheKey] && (new Date().getTime() - this.transformCache[cacheKey].timestamp) < 60000 && this.transformCache[cacheKey].content && this.transformCache[cacheKey].content.length) {
      return { content: this.transformCache[cacheKey].content, navItem: this.transformCache[cacheKey].navItem };
    }
    this.url = url;
    const navItem = this.navService.initItemByPath(this.url, parentUrl);
    if (!navItem) {
      console.warn('could not convert to item by url:', url);
      return { content: undefined, navItem: null };
    }
    let content = this.getTransformedEntry(navItem.entry);
    this.transformCache[cacheKey] = { content: _.cloneDeep(content), navItem, timestamp: new Date().getTime() };
    return { content: content as any, navItem };
  }

  ngOnDestroy() {
    this.readySubscription.unsubscribe();
  }

  hasContent(content): boolean {
    const item: any = this.getTransformedEntry(content.navigationItem.entry);
    switch (content.navigationItem.entry.detail.contentType) {
      case 'information':
        const media = item?.media?.length ? item.media : [];
        const documents = item?.documents?.length ? item.documents : [];
        const hasMap = item?.latitude && item?.longitude;
        const hasContact = item?.phone || item?.email || item?.website;
        const hasAddress = item?.street || (item?.zip && item?.city);
        return !!hasAddress || !!hasContact || !!documents.length || !!item?.description?.length || !!hasMap || !!media?.length;
      case 'gallery': case 'memory':
        return !!(item?.media?.length ? item.media : []).length;
      case 'event': case 'restaurant': case 'accommodation': case 'news': case 'opening-state': case 'important-numbers': case 'gastronomy': case 'partner':
        return !!item?.length;
      case 'webcam':
        return !!item?.length && !Array.isArray(item[0]);
      case 'mountain-railway':
        return item && (item?.railways?.length || item?.slopes?.length);
      case 'mountain-railway-new':
        return !!item?.elements?.length;
      case 'train': case 'bus':
        return item && (item?.arrivals?.length && item?.departures?.length);
      case 'highscore':
        return true;
      case 'weather':
        return !!item?.days?.length;
      default:
        if (!!content.navigationItem.entry.detail.contentType) {
          console.warn('no "has content" implementation for:', content.navigationItem.entry.detail.contentType, item);
        }
        return !!item;
    }
  }
}
